import GENERATED_LINKS from "../content/reports/generated-report-links.json";

// Trade-page → flagship report deep links. Kept separate from lib/reports.ts so
// pages that only need a link (homepage PreviewStudio, trade pages) don't pull
// the full report JSON payloads into their bundle.
export const REPORT_BY_TRADE_SLUG: Record<string, string> = {
  "plumbing-heating": "boiler-installation",
  electrical: "consumer-unit-replacement",
  roofing: "pitched-roof-recover",
  scaffolding: "scaffold-erection",
  groundworks: "excavation",
};

export function builderHref(tradeSlug: string): string {
  const report = REPORT_BY_TRADE_SLUG[tradeSlug];
  return report ? `/builder/?report=${report}` : "/builder/";
}

/** Builder reports that are NOT library templates (the two trade flagships) —
 *  lightweight metadata so listing pages (trade hubs, template library) can
 *  show them alongside TemplateCards without importing the full report JSON
 *  payloads. Every library template now has its own report (TEMPLATE_REPORT
 *  below), so its card links via the template page instead. */
export interface BuilderReportMeta {
  report: string;
  label: string;
  blurb: string;
  tradeSlug: string;
  /** Library category the report surfaces under in /templates/. */
  category: string;
}

export const BUILDER_REPORTS: BuilderReportMeta[] = [
  { report: "boiler-installation", label: "Boiler installation", blurb: "Install or replace domestic and light-commercial boilers — flue work at height, gas escape and hot works covered.", tradeSlug: "plumbing-heating", category: "hot-works" },
  { report: "consumer-unit-replacement", label: "Consumer unit replacement", blurb: "Replace consumer units / distribution boards — safe isolation, dead testing and Part P notification covered.", tradeSlug: "electrical", category: "electrical" },
];

export const BUILDER_REPORTS_BY_TRADE: Record<string, BuilderReportMeta[]> = BUILDER_REPORTS.reduce(
  (acc, r) => {
    (acc[r.tradeSlug] ||= []).push(r);
    return acc;
  },
  {} as Record<string, BuilderReportMeta[]>,
);

/** Library template → live builder report. Every template in lib/library.ts
 *  maps to a report; the two COSHH SEO twins share one report. Keep in step
 *  with lib/reports.ts (validated by scripts/check-reports.mjs). */
const HAND_TEMPLATE_REPORT: Record<string, string> = {
  // Work at height
  "wah-general": "working-at-height",
  "wah-mobile-tower": "mobile-tower-scaffold",
  "wah-mewp": "mewp-cherry-picker",
  "wah-ladders": "ladders-stepladders",
  "wah-scaffold-erect": "scaffold-erection",
  "wah-roof-pitched": "pitched-roof-recover",
  "wah-fragile-roof": "fragile-roof-work",
  "wah-gutter": "gutter-cleaning",
  "wah-fall-arrest": "fall-arrest-harness",
  "wah-edge-protection": "edge-protection",
  // Electrical
  "elec-installation": "electrical-installation",
  "elec-live-working": "working-near-live-services",
  "elec-temp-supply": "temporary-site-electrics",
  "elec-overhead-lines": "overhead-power-lines",
  "elec-consumer-unit": "consumer-unit-replacement",
  // Groundworks
  "ground-excavation": "excavation",
  "ground-trenching": "trenching-shoring",
  "ground-confined-space": "confined-space-entry",
  "ground-drainage": "drainage-installation",
  "ground-underground-services": "underground-services",
  // Lifting & plant
  "lift-crane": "crane-lifting-operations",
  "lift-telehandler": "telehandler",
  "lift-excavator": "excavator-operation",
  "lift-dumper": "site-dumper",
  "lift-traffic-mgmt": "site-traffic-management",
  "lift-manual-handling": "manual-handling",
  // Hot works
  "hot-welding": "welding",
  "hot-cutting-grinding": "cutting-grinding",
  "hot-roofing-torch": "torch-on-roofing",
  "hot-boiler-install": "boiler-installation",
  // COSHH & substances (both SEO twins share the coshh-assessment report)
  "coshh-general": "coshh-assessment",
  "coshh-risk-assessment": "coshh-assessment",
  "coshh-silica": "silica-dust",
  "coshh-cement": "cement-concrete",
  "coshh-solvents": "solvents-paints",
  "coshh-lead": "lead-paint",
  // Demolition & strip-out
  "demo-soft-strip": "soft-strip",
  "demo-structural": "structural-demolition",
  "demo-asbestos-awareness": "asbestos-awareness",
  // Trades & finishing
  "trade-bricklaying": "bricklaying-blockwork",
  "trade-steel-fixing": "steel-fixing",
  "trade-formwork": "formwork-shuttering",
  "trade-concreting": "concrete-pour",
  "trade-joinery": "carpentry-joinery",
  "trade-plastering": "plastering",
  "trade-painting": "painting-decorating",
  "trade-flooring": "floor-laying",
  "trade-tiling": "tiling",
  // Site & general
  "site-setup": "site-setup-welfare",
  "site-occupied": "occupied-premises",
  "site-lone-working": "lone-working",
  "site-noise-havs": "noise-vibration",
  "site-temp-works": "temporary-works",
};

// Generated builder reports (content-engine `export:reports`) fill every template
// the hand-authored set doesn't cover, so every library template is buildable. The
// hand-authored mapping wins on any conflict.
export const TEMPLATE_REPORT: Record<string, string> = { ...(GENERATED_LINKS as Record<string, string>), ...HAND_TEMPLATE_REPORT };

/** Generated report slugs that are actually in use — i.e. for templates with no
 *  hand-authored report. lib/reports.ts and lib/triggers.ts register only these,
 *  so the hand-authored reports stay the single source for their templates. */
export const GENERATED_ACTIVE_SLUGS: Set<string> = new Set(
  Object.entries(GENERATED_LINKS as Record<string, string>)
    .filter(([id]) => !(id in HAND_TEMPLATE_REPORT))
    .map(([, slug]) => slug),
);
